import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { baseURL } from '../../../../helpers/baseURL';
import { headers } from '../../../../helpers/Requests';
import failAlert from '../../../../helpers/failAlert';
import keywordBadgeStyle from '../../../../helpers/keywordBadgeStyle';

const MANUAL_INBOX = 'manual';

const AutoTransferTestModal = ({ show, onHide, rule }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [inboxes, setInboxes] = useState([]);
    const [inboxesLoading, setInboxesLoading] = useState(false);
    const [simulatedInbox, setSimulatedInbox] = useState('');

    useEffect(() => {
        if (!show) {
            setTitle('');
            setDescription('');
            setResult(null);
            setSimulatedInbox('');
            return;
        }

        const ruleInboxIds = (rule?.email_ids || []).map(Number);
        setSimulatedInbox(ruleInboxIds[0] ? String(ruleInboxIds[0]) : '');

        let cancelled = false;
        const loadInboxes = async () => {
            setInboxesLoading(true);
            try {
                const { data } = await axios.get(`${baseURL}emails`, headers);
                const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
                if (!cancelled) {
                    setInboxes(list);
                }
            } catch (err) {
                if (!cancelled) {
                    setInboxes(rule?.emails || []);
                }
            } finally {
                if (!cancelled) {
                    setInboxesLoading(false);
                }
            }
        };
        loadInboxes();
        return () => {
            cancelled = true;
        };
    }, [show, rule]);

    const inboxOptions = useMemo(() => {
        const ruleInboxIds = (rule?.email_ids || []).map(Number);
        const scoped = ruleInboxIds.length > 0;
        const options = [];

        if (!scoped) {
            options.push({ value: '', label: 'Every incoming email' });
        }

        inboxes.forEach((email) => {
            options.push({
                value: String(email.id),
                label: email.email_address || email.name || `#${email.id}`,
                isRuleInbox: scoped ? ruleInboxIds.includes(Number(email.id)) : true,
            });
        });

        if (scoped) {
            options.push({ value: MANUAL_INBOX, label: 'Manual ticket (no inbox)' });
        }

        return options;
    }, [inboxes, rule]);

    const runTest = async (e) => {
        e.preventDefault();
        if (!rule) return;
        setRunning(true);
        setResult(null);
        try {
            const payload = { title, description };
            if (simulatedInbox === MANUAL_INBOX) {
                payload.source_email_id = null;
            } else if (simulatedInbox !== '') {
                payload.source_email_id = Number(simulatedInbox);
            }

            const { data } = await axios.post(
                `${baseURL}auto-transfer-rule-test/${rule.id}`,
                payload,
                headers
            );
            setResult(data);
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to run test.');
        } finally {
            setRunning(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Form onSubmit={runTest}>
                <Modal.Header closeButton>
                    <Modal.Title>Test rule: {rule?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small">
                        Paste sample ticket text and pick an inbox to simulate live routing, nothing is created or transferred.
                    </p>
                    <Form.Group className="mb-3">
                        <Form.Label>Simulate incoming email</Form.Label>
                        {inboxesLoading ? (
                            <div className="py-2">
                                <Spinner animation="border" size="sm" />
                            </div>
                        ) : (
                            <Form.Select
                                value={simulatedInbox}
                                onChange={(e) => setSimulatedInbox(e.target.value)}
                            >
                                {inboxOptions.map((option) => (
                                    <option key={option.value || 'all'} value={option.value}>
                                        {option.label}
                                        {option.isRuleInbox === false ? ' (not in this rule)' : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Ticket title"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ticket description"
                        />
                    </Form.Group>

                    {result ? (
                        <div className="border rounded p-3">
                            <div className="mb-2 d-flex flex-wrap gap-2">
                                {result.would_transfer ? (
                                    <Badge bg="success">Would transfer</Badge>
                                ) : result.transfer_block ? (
                                    <Badge bg="warning" text="dark">Matches, but blocked</Badge>
                                ) : (
                                    <Badge bg="secondary">Would not transfer</Badge>
                                )}
                                {result.rule_is_active === false ? (
                                    <Badge bg="danger">Rule inactive</Badge>
                                ) : null}
                            </div>
                            {/* Shown even when AI rejects: the rule being switched off is the
                                reason it can never fire, whatever the rest of the result says. */}
                            {result.rule_is_active === false ? (
                                <div className="small text-danger mb-2">
                                    This rule is switched off, so it never runs on live tickets. Turn it on to use it.
                                </div>
                            ) : null}
                            {result.simulated_inbox ? (
                                <div className="small text-muted mb-2">
                                    Simulated inbox: {result.simulated_inbox.email_address}
                                </div>
                            ) : (
                                <div className="small text-muted mb-2">
                                    Simulated as {result.inbox_skipped ? 'a manual ticket with no inbox' : 'every inbox'}
                                </div>
                            )}
                            {result.inbox_skipped ? (
                                <div className="small text-danger mb-2">
                                    This rule does not run for the selected inbox, so AI is not asked — same as live.
                                </div>
                            ) : null}
                            {result.matched_keywords?.length > 0 ? (
                                <div className="mb-2">
                                    <div className="small text-muted mb-1">Keywords found (context only, doesn't gate AI)</div>
                                    <div className="d-flex flex-wrap gap-1">
                                        {result.matched_keywords.map((k) => (
                                            <Badge key={k} bg="secondary" style={keywordBadgeStyle}>
                                                {k}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {!result.inbox_skipped ? (
                                <div className="mb-2">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <span className="small text-muted">AI check</span>
                                        {result.ai_error ? (
                                            <Badge bg="danger">Failed</Badge>
                                        ) : (
                                            <Badge bg={result.ai_match ? 'success' : 'secondary'}>
                                                {result.ai_match ? 'Confirmed' : 'Rejected'}
                                            </Badge>
                                        )}
                                    </div>
                                    {result.ai_prompt ? (
                                        <div className="small text-muted fst-italic mb-1">&ldquo;{result.ai_prompt}&rdquo;</div>
                                    ) : null}
                                    {result.ai_error ? (
                                        <div className="small text-danger">
                                            {result.ai_error} The ticket was not sent, to avoid guessing.
                                        </div>
                                    ) : (
                                        <div className="small text-muted">{result.ai_reason}</div>
                                    )}
                                </div>
                            ) : null}
                            {result.transfer_block ? (
                                <div className="small text-danger">{result.transfer_block}</div>
                            ) : null}
                            {result.would_transfer && result.destination ? (
                                <div className="small">
                                    Would transfer to <strong>{result.destination.name}</strong>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                    <Button type="submit" variant="primary" disabled={running || (!title.trim() && !description.trim())}>
                        {running ? 'Running…' : 'Run Test'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AutoTransferTestModal;
